import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

import { campaign } from '@/constants/campaign'

interface CtaLinkProps {
    href: string
    children: ReactNode
    external?: boolean
    className?: string
}

const CTA_CLASSES = cn(
    'group inline-flex h-12 items-center justify-center gap-3 rounded-sm bg-primary px-7',
    'font-heading text-[0.9375rem] font-bold leading-none tracking-[0.2em] text-primary-foreground',
    'transition-[filter,box-shadow] duration-300 ease-out hover:shadow-[0_14px_44px_-14px_var(--primary)] hover:brightness-110 active:brightness-95',
    'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white',
    'sm:h-14 sm:px-9 sm:text-base'
)

// The red campaign button. External links open in a new tab and say so to screen readers.
const CtaLink: FC<CtaLinkProps> = ({ href, children, external = false, className }): ReactNode => {
    const label = (
        <span className='-mr-[0.2em]'>
            {children}
        </span>
    )

    if (!external) {
        return (
            <Link href={href} className={cn(CTA_CLASSES, className)}>
                {label}
            </Link>
        )
    }

    return (
        <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            className={cn(CTA_CLASSES, className)}
        >
            {label}

            <span className='sr-only'>
                {` ${campaign.accessibility.newTabHint}`}
            </span>

            <ArrowUpRight
                aria-hidden='true'
                strokeWidth={2.25}
                className='size-4 transition-transform duration-300 ease-out motion-safe:group-hover:translate-x-0.5 motion-safe:group-hover:-translate-y-0.5'
            />
        </a>
    )
}

export default CtaLink
